import { logger } from 'common/services/logger';

const STORAGE_VERSION = 'v1';
const KEY_PREFIX = `natter:forcedLogout:keptCopy:${STORAGE_VERSION}:`;

/** A copy is offered back for a day; after that it is treated as gone. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// A forced logout can try to write several times on one page, so a broken storage is reported once
// for each kind of failure.
let hasWarnedWriteFailure = false;
let hasWarnedAccessFailure = false;

/** Reading or removing failed: a copy may be left behind, which is worth knowing once. */
const warnAccessFailure = (error: unknown) => {
  if (hasWarnedAccessFailure) return;

  hasWarnedAccessFailure = true;
  logger.warn('[KeptCopy] Could not read or remove a kept copy', { error });
};

export type KeptCopy = {
  accountId: number;
  scope: string;
  takenAt: number;
  payload: unknown;
};

const keyFor = (accountId: number, scope: string) => `${KEY_PREFIX}${accountId}:${scope}`;

const parse = (raw: string | null): KeptCopy | undefined => {
  if (!raw) return undefined;

  try {
    const stored = JSON.parse(raw) as Partial<KeptCopy> | null;
    if (typeof stored?.accountId !== 'number' || typeof stored.scope !== 'string') return undefined;
    if (typeof stored.takenAt !== 'number' || !('payload' in stored)) return undefined;

    return stored as KeptCopy;
  } catch {
    return undefined;
  }
};

const isExpired = ({ takenAt }: KeptCopy) => Date.now() - takenAt > MAX_AGE_MS;

const removeKey = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    warnAccessFailure(error);
  }
};

/**
 * Removes every stored copy `shouldRemove` picks. `length`/`key(i)` rather than `Object.keys`, and
 * the list is collected before anything is removed, as in `sweepOrphanedCustomBackgrounds`.
 */
const sweep = (shouldRemove: (copy: KeptCopy | undefined) => boolean) => {
  try {
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
      (key): key is string => !!key && key.startsWith(KEY_PREFIX)
    );

    keys.filter((key) => shouldRemove(parse(localStorage.getItem(key)))).forEach(removeKey);
  } catch (error) {
    logger.error('[KeptCopy] Could not sweep kept copies', error);
  }
};

/**
 * Keeps a copy of unsaved work through a forced logout, in localStorage so a sign-in that comes back
 * in a new tab (a magic link) can still read it. Tagged with the account it was taken for. Returns
 * whether the copy was stored; best-effort, never throws.
 */
export const writeKeptCopy = (accountId: number, scope: string, payload: unknown): boolean => {
  try {
    localStorage.setItem(
      keyFor(accountId, scope),
      JSON.stringify({ accountId, scope, takenAt: Date.now(), payload } satisfies KeptCopy)
    );

    return true;
  } catch (error) {
    if (!hasWarnedWriteFailure) {
      hasWarnedWriteFailure = true;
      logger.warn('[KeptCopy] Could not keep a copy through the forced logout', { error });
    }

    return false;
  }
};

/** The account's copy for this scope, or `undefined`. An expired or unreadable copy is removed. */
export const readKeptCopy = (accountId: number, scope: string): KeptCopy | undefined => {
  const key = keyFor(accountId, scope);

  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch (error) {
    warnAccessFailure(error);

    return undefined;
  }
  if (!raw) return undefined;

  const copy = parse(raw);
  if (!copy || isExpired(copy)) {
    removeKey(key);

    return undefined;
  }

  return copy.accountId === accountId ? copy : undefined;
};

export const removeKeptCopy = (accountId: number, scope: string): void => removeKey(keyFor(accountId, scope));

/** Run on a logout the user chose: nothing kept for a forced logout outlives it. */
export const removeAllKeptCopies = (): void => sweep(() => true);

/** Run when an account signs in, so a shared browser never holds another person's unsaved work. */
export const removeKeptCopiesNotOwnedBy = (accountId: number): void => sweep((copy) => copy?.accountId !== accountId);

/** Copies no page ever opened again would otherwise outlive their 24 hours. */
export const removeExpiredKeptCopies = (): void => sweep((copy) => !copy || isExpired(copy));

/**
 * Calls `listener` with the current copy whenever another tab changes it. The `storage` event never
 * fires in the tab that made the change.
 */
export const subscribeToKeptCopy = (
  accountId: number,
  scope: string,
  listener: (copy: KeptCopy | undefined) => void
): (() => void) => {
  const key = keyFor(accountId, scope);
  const handleStorage = (event: StorageEvent) => {
    // A `null` key is another tab clearing the whole storage.
    if (event.key === key || event.key === null) listener(readKeptCopy(accountId, scope));
  };

  window.addEventListener('storage', handleStorage);

  return () => window.removeEventListener('storage', handleStorage);
};
